import { Injectable } from '@angular/core';
import { Order } from '../../dto/order';
import { CrudService } from '../../services';
import { HttpParams, HttpClient, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { DataListModel } from '../../models/data-list-model';
import { toServerDateString, toISODateString } from '../../providers/app-date-adapter';
import { TotalCardOrderLightStatus } from '../../dto/total-card-order-light-status';
import { OrderCancellationModel } from '../../models/order-cancellation-model';
import { EnumModel } from '../../models/enum-model';
import { CardReorderModel } from '../../models/card-reorder-model';
import { DateTableFilter } from '../../table-datasouces/date-table-filter';
import { PrevNextModel } from '../../models/prev-next-model';
import {TotalCardOrder} from '../../dto/total-card-order';
import {CorporateForm} from '../../dto/corporate-form';
import {TotalStatsCard, TotalStatsProduct, TotalStatsTransaction} from '../../dto/total-stats-transaction';

@Injectable({
  providedIn: 'root'
})
export class TotalCardOrdersService implements CrudService<TotalCardOrder> {
  readonly apiPath = 'total-card-orders/';

  constructor(private http: HttpClient) { }
  get(id: any) {
    return this.http.get(this.apiPath + id) as Observable<TotalCardOrder>;
  }

  getMany(
    pageIndex: number,
    pageSize: number,
    sortField: string,
    sortDirection: string,
    filterString: string,
    dateFilterCols: string[],
    startFilterDate: Date,
    endFilterDate: Date,
    someAdditionalData: string,
    additionalFilterData?: string[],
    enumFilter?: { key: string; value: string }

  ): Observable<DataListModel<TotalCardOrder>> {
    let params = new HttpParams()
      .set('page', pageIndex.toString())
      .set('pageSize', pageSize.toString())
      .set('filterString', filterString)
      .set('sortField', sortField)
      .set('sortDirection', sortDirection)
      .set('startFilterDate', toServerDateString(startFilterDate))
      .set('endFilterDate', toServerDateString(endFilterDate))
      .set('someAdditionalData', someAdditionalData);

    if (dateFilterCols instanceof Array) {
      dateFilterCols.forEach(element => {
        params = params.append('dateFilterCols', element);
      });
    }
    if (additionalFilterData instanceof Array) {
      additionalFilterData.forEach(element => {
        params = params.append('additionalFilterData', element);
      });
    }
    if (enumFilter) {
      params = params.append('enumFilter', JSON.stringify(enumFilter));
    }

    return this.http.get(this.apiPath, { params }) as Observable<DataListModel<TotalCardOrder>>;
  }
  add(item: TotalCardOrder): Observable<Object> {
    throw new Error('Method not implemented.');
  }
  update(item: TotalCardOrder) {
    return this.http.put(this.apiPath, item) as Observable<TotalCardOrder>;
  }

  getCorporateForms(): Observable<any> {
    return this.http.get(this.apiPath + 'corporate-forms') as Observable<CorporateForm[]>;
  }

  getCancellationReasons(): Observable<EnumModel[]> {
    return this.http.get(this.apiPath + 'cancellation-reasons') as Observable<EnumModel[]>;
  }

  cancelOrder(orderCancellationModel: OrderCancellationModel) {
    return this.http.post(this.apiPath + orderCancellationModel.orderId + '/cancel', orderCancellationModel);
  }

  delete(id: any) {
    return this.deleteMany([id]);
  }

  restore(id: any) {
    return this.restoreMany([id]);
  }

  export(idList: any[]): Observable<HttpResponse<Blob>> {
    let httpParams = new HttpParams();

    idList.forEach(id => {
      httpParams = httpParams.append('id', id.toString());
    });

    return this.http.get(this.apiPath + 'export', { params: httpParams, responseType: 'blob', observe: 'response' });
  }

  getStatuses(idList: any[]): Observable<TotalCardOrderLightStatus[]> {
    let httpParams = new HttpParams();

    idList.forEach(id => {
      httpParams = httpParams.append('id', id.toString());
    });

    return this.http.get(this.apiPath + 'status', { params: httpParams }) as Observable<TotalCardOrderLightStatus[]>;
  }

  deleteMany(idList: any[]) {
    let httpParams = new HttpParams();

    idList.forEach(id => {
      httpParams = httpParams.append('list', id.toString());
    });

    return this.http.delete(this.apiPath, { params: httpParams });
  }

  restoreMany(idList: any[]) {
    let httpParams = new HttpParams();
    idList.forEach(id => {
      httpParams = httpParams.append('list', id.toString());
    });
    return this.http.patch(this.apiPath, null, { params: httpParams });
  }

  getFilterParams(filters: DateTableFilter): HttpParams {
    const str = filters.str || '';
    const dateFilterCols = filters.dateFilterCols || [];
    const startFilterDate = filters.startFilterDate || null;
    const endFilterDate = filters.endFilterDate || null;

    let params = new HttpParams()
      .set('filterString', str)
      .set('startFilterDate', toISODateString(startFilterDate))
      .set('endFilterDate', toISODateString(endFilterDate));
    if (filters.sortField) {
      params = params.set('sortField', filters.sortField);
    }
    if (filters.sortDirection) {
      params = params.set('sortDirection', filters.sortDirection);
    }


    if (dateFilterCols instanceof Array) {
      dateFilterCols.forEach(element => {
        params = params.append('dateFilterCols', element);
      });
    }

    return params;
  }

  getPrev(filters: DateTableFilter, orderId: number): Observable<number> {
    const params = this.getFilterParams(filters);
    return this.http.get(`${this.apiPath}${orderId}/prev`, { params }) as Observable<number>;
  }

  getNext(filters: DateTableFilter, orderId: number): Observable<number> {
    const params = this.getFilterParams(filters);
    return this.http.get(`${this.apiPath}${orderId}/next`, { params }) as Observable<number>;
  }

  getPrevNext(filters: DateTableFilter, orderId: number): Observable<PrevNextModel> {
    const params = this.getFilterParams(filters);
    return this.http.get(`${this.apiPath}${orderId}/prev-next`, { params }) as Observable<PrevNextModel>;
  }

  exportAllData(filters: DateTableFilter): Observable<HttpResponse<Blob>> {
    const params = this.getFilterParams(filters);
    return this.http.get(`${this.apiPath}export-all`, { params, responseType: 'blob', observe: 'response' }) as Observable<HttpResponse<Blob>>;
  }

  getPdfFormAttachment(totalCardOrderId: number): Observable<HttpResponse<Blob>> {
      return this.http.get( this.apiPath + totalCardOrderId + '/pdf-form', { params: null, responseType: 'blob', observe: 'response' } );
  }

  getCustomerTotalCards(totalCardOrderId: number): Observable<TotalStatsCard[]> {
    return this.http.get(this.apiPath + totalCardOrderId + '/total-cards') as Observable<TotalStatsCard[]>;
  }


  getProductsForTotalCard(totalStatCardId: number) {
    return this.http.get(this.apiPath + totalStatCardId + '/history-products') as Observable<TotalStatsProduct[]>;
  }

  getTransactionsForTotalCard(totalStatCardId: number, totalStatProductId: number, limit: number) {
    return this.http.get(this.apiPath + totalStatCardId + '/history-product/' + totalStatProductId + '/transactions/' + limit) as Observable<TotalStatsTransaction[]>;
  }

  getTransactionsForTotalCardTotalAmountNet(totalStatCardId: number, totalStatProductId: number): Observable<number> {
    return this.http.get(this.apiPath + totalStatCardId + '/history-product/' + totalStatProductId + '/transaction-total-amount-net') as Observable<number>;
  }

  getTransactionsForTotalCardTotalQuantity(totalStatCardId: number, totalStatProductId: number): Observable<number> {
    return this.http.get(this.apiPath + totalStatCardId + '/history-product/' + totalStatProductId + '/transaction-total-quantity') as Observable<number>;
  }
}
